import { format, isValid, parse } from "date-fns";

export const DEFAULT_TIMEZONE = "Asia/Kolkata";
export const getTimezone = (tzName?: string | null): string => tzName || DEFAULT_TIMEZONE;

/**
 * Normalizes a time string to "HH:mm", accepting both "HH:mm:ss" and "HH:mm" inputs.
 * Returns an empty string for falsy or unparseable values (no crash on invalid dates).
 * @param time - The time string to normalize (string, null, or undefined)
 * @returns Time formatted as "HH:mm", or empty string if missing/invalid
 */
export const normalizeToHHmm = (time: string | undefined | null): string => {
  if (!time) return "";
  const parsed = parse(time, "HH:mm:ss", new Date());
  if (isValid(parsed)) return format(parsed, "HH:mm");
  const parsedShort = parse(time, "HH:mm", new Date());
  if (isValid(parsedShort)) return format(parsedShort, "HH:mm");

  return "";
};

/**
 * Formats a date for display in event/competition dropdown labels.
 * @param date - The date to format (string, Date, null, or undefined)
 * @returns Formatted date string (e.g., "31 Mar 2026") or empty string if no date
 */
export const formatEventDate = (date: string | Date | null | undefined): string => {
  if (!date) return "";

  return new Date(date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

/**
 * Human-readable date-time, e.g. "29th June 2026, 01:30 PM".
 * @returns Formatted string, or empty string if missing/invalid.
 */
export const formatHumanDateTime = (date: string | number | Date | null | undefined, tz?: string | null): string => {
  if (!date) return "";
  const d = new Date(date);
  if (!isValid(d)) return "";
  // Render in the operator's timezone when provided (chat timestamps are UTC and
  // must not drift to the agent's browser locale); otherwise keep local formatting.
  // A malformed/unknown tz makes Intl throw a RangeError — fall back to local
  // formatting rather than crashing the render.
  if (tz) {
    try {
      return new Intl.DateTimeFormat("en-GB", { timeZone: tz, day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true }).format(d);
    } catch {

      /* invalid timezone — fall through to local formatting */
    }
  }

  return format(d, "do MMMM yyyy, hh:mm a");
};

/**
 * Human-readable time only, e.g. "01:30 PM".
 * @returns Formatted string, or empty string if missing/invalid.
 */
export const formatHumanTime = (date: string | number | Date | null | undefined, tz?: string | null): string => {
  if (!date) return "";
  const d = new Date(date);
  if (!isValid(d)) return "";
  if (tz) {
    try {
      return new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: true }).format(d);
    } catch {

      /* invalid timezone — fall through to local formatting */
    }
  }

  return format(d, "hh:mm a");
};

/**
 * Elapsed time since a date as a single unit — seconds, minutes, hours, days,
 * or weeks (e.g. "45s", "12m", "3h", "2d", "1w") — instead of a wall-clock
 * time. Used for "waiting since" style displays where how long ago something
 * happened matters more than the actual clock time.
 * @returns Formatted duration, or empty string if missing/invalid.
 */
export const formatWaitingDuration = (date: string | number | Date | null | undefined, now: Date = new Date()): string => {
  if (!date) return "";
  const d = new Date(date);
  if (!isValid(d)) return "";

  const diffSeconds = Math.max(0, Math.floor((now.getTime() - d.getTime()) / 1000));
  if (diffSeconds < 60) return `${diffSeconds}s`;

  const minutes = Math.floor(diffSeconds / 60);
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;

  const weeks = Math.floor(days / 7);

  return `${weeks}w`;
};

/**
 * Returns the start of the current day in UTC (midnight 00:00:00.000Z).
 * Used for filtering events/competitions to today and future only.
 * @returns Date object set to midnight UTC of the current day
 */
export const getStartOfDayUTC = (): Date => {
  const now = new Date();

  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0));
};
